'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { createContext, ReactNode, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../libs/utils';
import { Icon } from '../icon/Icon';
import { List } from '../list/List';

const VIEWPORT_PADDING_4PX = 4;

/** 주어진 좌표 (x, y)가 뷰포트를 벗어나지 않도록 보정하여 요소에 적용 */
function adjustToViewport(el: HTMLElement, x: number, y: number) {
  const { width, height } = el.getBoundingClientRect();

  const adjustedX = Math.max(VIEWPORT_PADDING_4PX, Math.min(x, window.innerWidth - width - VIEWPORT_PADDING_4PX));
  const adjustedY = Math.max(VIEWPORT_PADDING_4PX, Math.min(y, window.innerHeight - height - VIEWPORT_PADDING_4PX));

  el.style.left = `${adjustedX}px`;
  el.style.top = `${adjustedY}px`;
}

type Side = 'top' | 'bottom' | 'right' | 'left';
type Align = 'start' | 'center' | 'end';

function alignOnAxis(start: number, center: number, end: number, contentSize: number, actualAlign: Align): number {
  if (actualAlign === 'center') return center - contentSize / 2;
  if (actualAlign === 'end') return end - contentSize;
  return start;
}

/**
 * 크로스축(메인 side에 수직인 축)에서 뷰포트 안에 들어가도록 align을 고름.
 * 선호 정렬이 그대로 가능하면 유지하고, 아니면 end/start/center 순으로 시도한 뒤 overflow 합이 최소인 값을 택함.
 */
function resolveProximityAlign(
  preferred: Align,
  start: number,
  center: number,
  end: number,
  contentSize: number,
  viewportMin: number,
  viewportMax: number
): Align {
  const innerMin = viewportMin + VIEWPORT_PADDING_4PX;
  const innerMax = viewportMax - VIEWPORT_PADDING_4PX;

  const posFor = (a: Align) => alignOnAxis(start, center, end, contentSize, a);

  const fits = (a: Align) => {
    const pos = posFor(a);
    return pos >= innerMin && pos + contentSize <= innerMax;
  };

  if (fits(preferred)) return preferred;

  const tryOrder: Align[] = preferred === 'start' ? ['end', 'center', 'start'] : preferred === 'end' ? ['start', 'center', 'end'] : ['start', 'end', 'center'];

  for (const a of tryOrder) {
    if (fits(a)) return a;
  }

  let best: Align = preferred;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const a of ['start', 'center', 'end'] as const) {
    const pos = posFor(a);
    const overStart = Math.max(0, innerMin - pos);
    const overEnd = Math.max(0, pos + contentSize - innerMax);
    const score = overStart + overEnd;
    if (score < bestScore) {
      bestScore = score;
      best = a;
    }
  }
  return best;
}

/** anchorRect 기준으로 side/align에 따라 메뉴 좌표를 계산하고, 공간 부족 시 반대쪽으로 flip, 크로스축은 근접 align 보정 */
function calcPosition(anchorRect: DOMRect, contentWidth: number, contentHeight: number, side: Side, align: Align): { x: number; y: number } {
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const anchorCenterY = anchorRect.top + anchorRect.height / 2;

  let x: number;
  let y: number;
  let actualSide = side;

  if (actualSide === 'right' && anchorRect.right + contentWidth > window.innerWidth - VIEWPORT_PADDING_4PX) {
    actualSide = 'left';
  } else if (actualSide === 'left' && anchorRect.left - contentWidth < VIEWPORT_PADDING_4PX) {
    actualSide = 'right';
  } else if (actualSide === 'bottom' && anchorRect.bottom + contentHeight > window.innerHeight - VIEWPORT_PADDING_4PX) {
    actualSide = 'top';
  } else if (actualSide === 'top' && anchorRect.top - contentHeight < VIEWPORT_PADDING_4PX) {
    actualSide = 'bottom';
  }

  if (actualSide === 'right' || actualSide === 'left') {
    const actualAlign = resolveProximityAlign(align, anchorRect.top, anchorCenterY, anchorRect.bottom, contentHeight, 0, window.innerHeight);
    x = actualSide === 'right' ? anchorRect.right : anchorRect.left - contentWidth;
    y = alignOnAxis(anchorRect.top, anchorCenterY, anchorRect.bottom, contentHeight, actualAlign);
  } else {
    const actualAlign = resolveProximityAlign(align, anchorRect.left, anchorCenterX, anchorRect.right, contentWidth, 0, window.innerWidth);
    y = actualSide === 'bottom' ? anchorRect.bottom : anchorRect.top - contentHeight;
    x = alignOnAxis(anchorRect.left, anchorCenterX, anchorRect.right, contentWidth, actualAlign);
  }

  return { x, y };
}

interface MenuContextValue {
  closeMenu: () => void;
  activeSub: string | null;
  setActiveSub: (id: string | null) => void;
  focusSubOnOpen: React.RefObject<boolean>;
}

const MenuContext = createContext<MenuContextValue>({
  closeMenu: () => {},
  activeSub: null,
  setActiveSub: () => {},
  focusSubOnOpen: { current: false }
});

export interface MenuProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** 배치(getBoundingClientRect)에 사용. 없으면 triggerRef와 동일 */
  positionRef?: React.RefObject<HTMLElement | null>;
  side?: Side;
  align?: Align;
}

/**
 * 트리거 요소 기준으로 팝업되는 컨텍스트 메뉴 컴포넌트
 *
 * Portal을 사용하여 document.body에 렌더링되며, positionRef(또는 triggerRef) 기준으로 메뉴 위치를 계산합니다.
 *
 * @param open - 메뉴 표시 여부
 * @param onOpenChange - 메뉴 열림/닫힘 상태 변경 콜백
 * @param triggerRef - 외부 클릭/ESC 포커스 기준(보통 실제 트리거 버튼)
 * @param positionRef - 메뉴 좌표 계산용 앵커(없으면 triggerRef와 동일)
 * @param side - 메뉴가 열리는 방향 ('top' | 'bottom' | 'left' | 'right', 기본값: 'bottom')
 * @param align - 크로스축 기준 정렬 ('start' | 'center' | 'end', 기본값: 'start'). 뷰포트를 벗어나면 가능한 다른 align으로 근접 보정됨
 * @param className - 메뉴 컨테이너에 적용할 추가 클래스
 * @param children - MenuItem, MenuDivider, MenuSub 등의 메뉴 항목들
 *
 * 외부 클릭 시 자동으로 닫히며, ESC 키로도 닫을 수 있습니다.
 * 화면을 벗어나는 경우 자동으로 반대 방향 또는 근접 정렬로 보정됩니다.
 */
export const Menu = ({ children, open = false, onOpenChange, triggerRef, positionRef, side = 'bottom', align = 'start', className }: MenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const focusSubOnOpen = useRef(false);

  const closeMenu = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  // 메뉴가 닫히면 서브메뉴 초기화
  useEffect(() => {
    if (!open) {
      setActiveSub(null);
    }
  }, [open]);

  // 메뉴가 열린 동안 배경 스크롤 방지
  useEffect(() => {
    if (!open) return;

    const preventScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (menuRef.current && target && menuRef.current.contains(target)) return;
      e.preventDefault();
    };

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;

    const anchorEl = positionRef?.current ?? triggerRef?.current;
    if (!anchorEl) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const { width, height } = menuRef.current.getBoundingClientRect();
    const { x, y } = calcPosition(anchorRect, width, height, side, align);
    adjustToViewport(menuRef.current, x, y);
  }, [open, triggerRef, positionRef, side, align]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current || menuRef.current.contains(target)) {
        return;
      }

      const inTrigger = triggerRef?.current?.contains(target) ?? false;
      const inPositionAnchor = positionRef?.current?.contains(target) ?? false;

      if (!inTrigger && !inPositionAnchor) {
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onOpenChange, triggerRef, positionRef]);

  // 키보드 네비게이션: ↑↓ 이동, Home/End, Enter 실행, ←→ 서브메뉴 열기/닫기, Esc 닫기
  useEffect(() => {
    if (!open) return;

    const getItemsInMenu = (menuContainer: HTMLElement): HTMLElement[] => {
      return Array.from(menuContainer.querySelectorAll<HTMLElement>('[data-menu-item]:not([data-disabled])')).filter(
        (item) => item.closest('[data-menu-container]') === menuContainer
      );
    };

    const getCurrentMenu = (): HTMLElement | null => {
      return (document.activeElement as HTMLElement)?.closest<HTMLElement>('[data-menu-container]') ?? menuRef.current;
    };

    // 서브메뉴를 닫고 부모 트리거로 포커스 복귀
    const closeSubAndFocusTrigger = (subMenu: HTMLElement) => {
      const trigger = subMenu.closest('[data-menu-sub]')?.querySelector<HTMLElement>('[data-menu-sub-trigger]');
      setActiveSub(null);
      trigger?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentMenu = getCurrentMenu();
      if (!currentMenu) return;

      const items = getItemsInMenu(currentMenu);
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          items[next]?.focus();
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          items[prev]?.focus();
          break;
        }
        case 'Home': {
          event.preventDefault();
          items[0]?.focus();
          break;
        }
        case 'End': {
          event.preventDefault();
          items[items.length - 1]?.focus();
          break;
        }
        case 'Enter': {
          if (currentIndex >= 0) {
            event.preventDefault();
            items[currentIndex]?.click();
          }
          break;
        }
        case 'ArrowRight': {
          const focused = items[currentIndex];
          if (focused?.hasAttribute('data-menu-sub-trigger') && !focused.hasAttribute('data-menu-sub-open')) {
            event.preventDefault();
            focusSubOnOpen.current = true;
            focused.click();
          }
          break;
        }
        case 'ArrowLeft': {
          if (currentMenu !== menuRef.current) {
            event.preventDefault();
            closeSubAndFocusTrigger(currentMenu);
          }
          break;
        }
        case 'Escape': {
          event.preventDefault();
          onOpenChange?.(false);
          triggerRef?.current?.focus();
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onOpenChange, triggerRef, setActiveSub]);

  if (typeof window === 'undefined' || !open) return null;

  return createPortal(
    <MenuContext.Provider value={{ closeMenu, activeSub, setActiveSub, focusSubOnOpen }}>
      <div
        ref={menuRef}
        data-menu-container
        style={{ position: 'fixed', top: 0, left: 0, zIndex: 100 }}
        className={cn(
          'min-w-[188px] py-tiny-large px-normal-medium rounded-bzc-medium border border-neutral-border-high bg-white elevation-medium overscroll-contain',
          className
        )}
      >
        {children}
      </div>
    </MenuContext.Provider>,
    document.body
  );
};

const menuItemVariants = cva('', {
  variants: {
    variant: {
      default: '',
      danger: 'text-danger-foreground-main [&:hover]:bg-danger-background-low'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

export interface MenuItemProps extends VariantProps<typeof menuItemVariants> {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  autoClose?: boolean;
}

/**
 * 클릭 시 동작을 실행하고 메뉴를 자동으로 닫는 메뉴 항목
 *
 * @param variant - 'default' | 'danger' (기본값: 'default')
 * @param icon - 항목 좌측에 표시할 아이콘
 * @param onClick - 클릭 시 실행할 콜백
 * @param disabled - 비활성화 여부
 * @param className - 추가 클래스
 * @param autoClose - 클릭 시 메뉴 자동 닫힘 여부 (기본값: true). false로 설정하면 메뉴를 직접 닫아야 합니다.
 */
export const MenuItem = ({ children, icon, variant = 'default', onClick, disabled, className, autoClose = true }: MenuItemProps) => {
  const { closeMenu } = useContext(MenuContext);

  const handleClick = () => {
    onClick?.();
    if (autoClose) closeMenu();
  };

  return (
    <List
      size="xsmall"
      title={children}
      leadingElement={icon}
      primaryAction
      state={disabled ? 'disabled' : 'enabled'}
      onClick={disabled ? undefined : handleClick}
      className={cn('focus:bg-neutral-background-hovered&pressed focus:outline-none rounded-bzc-xsmall', menuItemVariants({ variant }), className)}
      data-menu-item
      tabIndex={disabled ? undefined : -1}
      {...(disabled && { 'data-disabled': '' })}
    />
  );
};

/** 메뉴 항목 그룹 사이의 구분선 */
export const MenuDivider = () => <div className="my-tiny-large h-px w-full bg-neutral-border-main" />;

interface MenuSubContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLDivElement | null>;
}

const MenuSubContext = createContext<MenuSubContextValue>({
  open: false,
  setOpen: () => {},
  triggerRef: { current: null }
});

export interface MenuSubProps {
  children: ReactNode;
}

/** 하위 메뉴를 가지는 항목의 컨테이너. MenuSubTrigger + MenuSubContent를 자식으로 사용 */
export const MenuSub = ({ children }: MenuSubProps) => {
  const subId = useId();
  const { activeSub, setActiveSub } = useContext(MenuContext);
  const triggerRef = useRef<HTMLDivElement>(null);

  const open = activeSub === subId;

  const setOpen = useCallback(
    (v: boolean) => {
      setActiveSub(v ? subId : null);
    },
    [subId, setActiveSub]
  );

  return (
    <MenuSubContext.Provider value={{ open, setOpen, triggerRef }}>
      <div className="relative" data-menu-sub>
        {children}
      </div>
    </MenuSubContext.Provider>
  );
};

export interface MenuSubTriggerProps {
  children: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * 클릭 시 하위 메뉴를 열고 닫는 트리거 항목. 우측에 chevron 아이콘이 표시됨
 *
 * @param icon - 항목 좌측에 표시할 아이콘
 * @param disabled - 비활성화 여부
 * @param className - 추가 클래스
 */
export const MenuSubTrigger = ({ children, icon, disabled, className }: MenuSubTriggerProps) => {
  const { open, setOpen, triggerRef } = useContext(MenuSubContext);

  const handleClick = () => {
    if (disabled) return;
    setOpen(!open);
  };

  return (
    <div ref={triggerRef} onClick={handleClick}>
      <List
        size="xsmall"
        title={children}
        leadingElement={icon}
        primaryAction
        state={disabled ? 'disabled' : 'enabled'}
        supportingElement={<Icon type="chevron-right" size={16} />}
        className={cn('focus:bg-neutral-background-hovered&pressed focus:outline-none', open && 'bg-neutral-background-hovered&pressed', className)}
        data-menu-item
        data-menu-sub-trigger
        tabIndex={disabled ? undefined : -1}
        {...(disabled && { 'data-disabled': '' })}
        {...(open && { 'data-menu-sub-open': '' })}
      />
    </div>
  );
};

export interface MenuSubContentProps {
  children: ReactNode;
  className?: string;
  side?: 'right' | 'left';
  align?: Align;
}

/**
 * 하위 메뉴의 콘텐츠 영역. 상위 MenuSubTrigger 기준으로 측면에 표시됨
 *
 * @param side - 하위 메뉴가 열리는 방향 ('right' | 'left', 기본값: 'right')
 * @param align - 크로스축 정렬 ('start' | 'center' | 'end', 기본값: 'start'). 뷰포트 밖이면 근접 보정
 * @param className - 추가 클래스
 */
export const MenuSubContent = ({ children, className, side = 'right', align = 'start' }: MenuSubContentProps) => {
  const { open, triggerRef } = useContext(MenuSubContext);
  const { focusSubOnOpen } = useContext(MenuContext);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const { width, height } = contentRef.current.getBoundingClientRect();
    const { x, y } = calcPosition(triggerRect, width, height, side, align);
    adjustToViewport(contentRef.current, x, y);
  }, [open, side, align, triggerRef]);

  // 키보드(ArrowRight)로 서브메뉴를 열었을 때만 첫 항목에 자동 포커스 (마우스 클릭 시에는 동작하지 않음)
  useEffect(() => {
    if (!focusSubOnOpen.current || !contentRef.current) return;
    focusSubOnOpen.current = false;
    const firstItem = contentRef.current.querySelector<HTMLElement>('[data-menu-item]:not([data-disabled])');
    firstItem?.focus();
  });

  if (!open) return null;

  return (
    <div
      ref={contentRef}
      data-menu-container
      style={{ position: 'fixed', top: 0, left: 0, zIndex: 100 }}
      className={cn('min-w-[188px] py-tiny-large px-normal-medium rounded-bzc-medium border border-neutral-border-high bg-white elevation-medium', className)}
    >
      {children}
    </div>
  );
};
